import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useLang } from "../i18n";
import { PROJECTS, IMAGES } from "../data/content";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/fx/Reveal";
import { EuropeMap } from "../components/fx/EuropeMap";

const FILTERS = [
  { key: "all", ro: "Toate", en: "All" },
  { key: "residential", ro: "Rezidențial", en: "Residential" },
  { key: "commercial", ro: "Comercial", en: "Commercial" },
  { key: "industrial", ro: "Industrial", en: "Industrial" },
  { key: "smart", ro: "Smart Home", en: "Smart Home" },
  { key: "automation", ro: "Automatizări", en: "Automation" },
];

const ProjectCard = ({ p, L, tall }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.4 }}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 ${tall ? "row-span-2" : ""}`}
      data-testid="project-card"
    >
      <img src={p.image} alt={L(p)?.title} loading="lazy" decoding="async" className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${tall ? "h-full min-h-[420px]" : "h-64"}`} />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <div className="flex items-center gap-1 text-xs text-white/60">
          <MapPin className="h-3.5 w-3.5 text-brand" /> {L(p)?.place}
        </div>
        <h3 className="mt-1 font-display text-lg font-bold text-white">{L(p)?.title}</h3>
      </div>
    </motion.div>
  );
};

export default function Portfolio() {
  const { lang, L, ui } = useLang();
  const [active, setActive] = useState("all");
  const filtered = PROJECTS.filter((p) => active === "all" || p.cat === active);

  return (
    <>
      <PageHero
        label={ui.nav.portfolio}
        title={lang === "ro" ? "Proiecte realizate cu precizie și responsabilitate." : "Projects delivered with precision and responsibility."}
        desc={lang === "ro" ? "O selecție de lucrări rezidențiale, comerciale și industriale — în România, Italia și Germania." : "A selection of residential, commercial and industrial work — across Romania, Italy and Germany."}
        image={IMAGES.controlRoom}
      />

      <section className="py-20 md:py-28">
        <div className="container-pdr">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setActive(f.key)}
                data-testid={`filter-${f.key}`}
                className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                  active === f.key
                    ? "border-glow-2/50 bg-brand text-ink"
                    : "border-white/15 text-white/60 hover:border-white/30 hover:text-white"
                }`}
              >
                {f[lang]}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-10 grid auto-rows-[1fr] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <ProjectCard key={`${p.cat}-${i}-${active}`} p={p} L={L} tall={i % 5 === 0} />
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Regions / counties per country */}
      <section className="relative border-t border-white/10 bg-surface1/40 py-24 md:py-32">
        <div className="pointer-events-none absolute inset-0 dot-bg opacity-20" />
        <div className="container-pdr relative">
          <SectionHeading
            label={lang === "ro" ? "Acoperire" : "Coverage"}
            title={lang === "ro" ? "Județe și regiuni în care am activat." : "Counties and regions we've worked in."}
            desc={lang === "ro" ? "Proiecte livrate în România, Italia și Germania." : "Projects delivered across Romania, Italy and Germany."}
          />

          <Reveal>
            <div className="mt-14">
              <EuropeMap lang={lang} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
